<template>
    <div class="page-content">
        <div class="page-content__header">
            <UserHeadline title="CompletedStudies.title">
                <HideFilters v-on:toggleFilters="toggleFilters" :filtersHidden="filtersHidden"/>
            </UserHeadline>
        </div>
        <div class="page-content__container">
            <CompletedFilter
                :completedCourses="completedCourses.courses"
                :completedLearningPaths="completedCourses.learningPaths"
                v-on:completed-courses-filtered="onFilterChange"
                :sorting="sorting"
                :learningPathSorting="learningPathSorting"
                :filtersHidden="filtersHidden"
            />
            <div class="completed-results">
                <div class="page-content__summary-container" v-if="completedCourses.learningPaths.length !== 0 || completedCourses.courses.length !== 0">
                    <div class="page-content__summary">
                        <CompletedInfoTags :filteredCourseList="filteredCourseList" class="completed-results__tags"/>
                        <TotemButton
                            @click="listCertificate"
                            :disabled="!areThereCertificatesInFiltered"
                            icon="certificate"
                            size="medium"
                            color="secondary"
                            class="completed-results__course-transcript"
                            :label="$t('CompletedSorting.load_certificate')"
                        />
                    </div>
                    <p class="page-content__description" v-html="$t('CompletedStudies.description')"></p>
                </div>
                <div class="result-container" v-if="$totemLang !== 'dk'">
                    <CompletedSorting
                        v-on:sorting-changed="learningPathSortingChange"
                        :completedCourses="filteredCourseList.learningPaths"
                        :title="$t('CompletedStudies.learningPathsTitle')"
                        type="learning_paths"
                    >
                        <template v-slot="item">
                            <StudiesCountInfo
                                :studies="filteredCourseList.learningPaths"
                                :text="$t('CompletedCourses.countLearningPathsInfoText')"
                                type="completed"
                            />
                        </template>
                    </CompletedSorting>
                    <ContentList :content="filteredLearningPaths" v-if="filteredLearningPaths.length !== 0" class="completed-results__content-list">
                        <template v-slot="{course: learningPath}">
                            <TotemLearningPathItemMosaic :learningPath="learningPath" :isCompletedView="true"/>
                        </template>
                    </ContentList>
                </div>
                <div class="result-container">
                    <CompletedSorting
                        v-on:sorting-changed="sortingChange"
                        :completedCourses="filteredCourseList.courses"
                        :title="$t('CompletedStudies.coursesTitle')"
                        type="courses"
                    >
                        <template v-slot="item">
                            <StudiesCountInfo
                                :studies="filteredCourseList.courses"
                                :text="$t('CompletedCourses.countCoursesInfoText')"
                                type="completed"
                            />
                        </template>
                    </CompletedSorting>
                    <ContentList :content="limitedCollection" v-if="limitedCollection.length !== 0">
                        <template v-slot="{course}">
                            <TotemCourseItemMosaic
                                :course="course"
                                :isCompletedView="true"
                            />
                        </template>
                    </ContentList>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import ContentList from 'components/user/shared/limitedcontent/ContentList'
    import CompletedFilter from 'components/user/completedcourses/CompletedFilter'
    import UserHeadline from 'components/user/UserHeadline'
    import HideFilters from 'components/user/shared/HideFilters.vue'
    import CompletedInfoTags from 'components/user/completedcourses/CompletedInfoTags.vue'
    import CompletedSorting from 'components/user/completedcourses/CompletedSorting'
    import completedCourses from 'components/shared/mixins/completedCourses'
    import StudiesCountInfo from 'components/user/shared/StudiesCountInfo'

    export default {
        components: {
            ContentList,
            CompletedFilter,
            HideFilters,
            UserHeadline,
            CompletedInfoTags,
            CompletedSorting,
            StudiesCountInfo
        },
        mixins: [completedCourses],
        data () {
            return {
                filtersHidden: true
            }
        },
        computed: {
            filteredCourseList () {
                return {
                    courses: this.limitedCollection,
                    learningPaths: this.filteredLearningPaths
                }
            },
            areThereCertificatesInFiltered () {
                return Object.keys(this.filteredCourseList).some(key => this.filteredCourseList[key].length > 0)
            },
            areThereCertificates () {
                return Object.keys(this.completedCourses).some(key => this.completedCourses[key].length > 0)
            }
        },
        methods: {
            toggleFilters () {
                this.filtersHidden = !this.filtersHidden
            },
            onFilterChange (filteredCourses, filteredLearningPaths) {
                this.onCourseListChange(filteredCourses)
                this.onLearningPathListChange(filteredLearningPaths)
            },
            listCertificate () {
                this.$store.dispatch('Certificates/createListCertificate', this.filteredCourseList)
            }
        },
        created () {
            this.$store.dispatch('CompletedCourses/loadCertificates')
        }
    }
</script>

<style lang="scss" scoped>
.page-content {
    &--wide {
        padding: 10px 0 0;

        [class*="mobile"] & {
            padding: 30px 20px;
        }
    }

    &__header {
        display: flex;
        flex-direction: row;
        gap: 15px;
        align-items: center;
    }

    &__summary-container {
        display: flex;
        flex-direction: column;
        padding: 20px;
        background: $concrete;
        margin: 20px 0 30px 0;
        border-radius: 4px;
        gap: 15px;

        [class*="mobile"] & {
            padding: 20px 10px;
        }
    }

    &__summary {
        display: flex;
        flex-direction: row;

        [class*="mobile_sm"] & {
            flex-direction: column;
        }
    }

    &__description {
        font-style: italic;

        [class*="mobile_sm"] & {
            font-size: 1.4rem;
            line-height: 1.8rem;
        }
    }
}

.completed-results {
    display: flex;
    flex-direction: column;

    [class*="mobile_sm"] & {
        justify-content: center;
    }

    &__course-transcript {
        margin-left: auto;

        [class*="mobile_sm"] & {
            margin: 20px 0 0 0;
        }
    }

    &__content-list {
        margin-bottom: 50px;
    }
}
</style>
